import { useMemo } from 'react';

import { useGetMakesBySlugs } from 'Stores/MakesStore/MakesStore';
import { useGetModelIds } from 'Stores/ModelsStore/ModelsStore';
import type { Filter, VehicleListFilterQuery } from 'Types/filter';
import formatQueryToFilters from 'Utilities/filterQuery/formatQueryToFilters';
import { getMakesIds } from 'Utilities/makeModel/makeModel.helpers';

/*
 * This hook is used to generate filters based on the url query.
 * This calculation is fairly complex as query especially for make and model.
 * Query is using human readable strings for make and models - make=audi&model=a4 whereas filters are using unique ids,
 * and there is quiet a lot of calculations needed to determine model ids as it may contain duplicates across
 * different makes.
 */
export const useCalculateFiltersFromQuery = (query: VehicleListFilterQuery): Filter => {
	const getModelIds = useGetModelIds();
	const getMakesBySlugs = useGetMakesBySlugs();

	const calculatedFiltersFromQuery = useMemo(() => {
		const uniqueMakes = Array.from(new Set(query?.make));
		const makesBySlugs = getMakesBySlugs(query?.make);
		const makeIds = getMakesIds(makesBySlugs);
		const modelNames = (Array.isArray(query?.model) ? query?.model : [query?.model]) as string[];
		const modelIds = getModelIds({ makeIds, modelNames });
		const queryWithModels = {
			...query,
			make: uniqueMakes,
			model: modelIds,
		};
		return formatQueryToFilters(queryWithModels) as Filter;
	}, [getMakesBySlugs, getModelIds, query]);

	return calculatedFiltersFromQuery;
};
